import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, ChevronDown, Plus, User as UserIcon, Settings2, Trash2, LogOut, Crown, Shield, Check } from "lucide-react";

const ROLE_ICON = { owner: Crown, admin: Shield, member: UserIcon };

export default function WorkspaceSwitcher() {
  const { user } = useAuth();
  const { workspaces, activeId, activeWorkspace, setActiveId, refresh } = useWorkspace();
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteInput, setInviteInput] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [renameVal, setRenameVal] = useState("");

  const myRole = activeWorkspace?.role;
  const isAdmin = myRole === "owner" || myRole === "admin";
  const isOwner = myRole === "owner";

  const loadMembers = useCallback(async () => {
    if (!activeId) return;
    try { const r = await api.get(`/workspaces/${activeId}/members`); setMembers(r.data.members); setInvites(r.data.invites); }
    catch { /* noop */ }
  }, [activeId]);

  useEffect(() => { if (adminOpen) { loadMembers(); setRenameVal(activeWorkspace?.name || ""); } }, [adminOpen, loadMembers, activeWorkspace]);

  const createWorkspace = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Give your workspace a name");
    try {
      const r = await api.post("/workspaces", { name });
      await refresh();
      setActiveId(r.data.id);
      setNewOpen(false); setNewName("");
      toast.success("Workspace created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };

  const invite = async () => {
    const emails = inviteInput.split(/[,\s]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
    if (!emails.length) return toast.error("Enter at least one email");
    try {
      const r = await api.post(`/workspaces/${activeId}/invite`, { emails, role: inviteRole });
      const { added, invited } = r.data.results;
      toast.success(`Added ${added.length}, invited ${invited.length}`);
      setInviteInput(""); loadMembers(); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); }
  };

  const setRole = async (uid, role) => {
    try { await api.put(`/workspaces/${activeId}/members/${uid}/role`, { role }); loadMembers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
  };

  const removeMember = async (uid) => {
    try { await api.delete(`/workspaces/${activeId}/members/${uid}`); loadMembers(); refresh(); toast.success("Member removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const rename = async () => {
    const name = renameVal.trim();
    if (!name) return;
    try { await api.put(`/workspaces/${activeId}`, { name }); refresh(); toast.success("Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
  };

  const leaveWorkspace = async () => {
    try { await api.delete(`/workspaces/${activeId}/members/${user.user_id}`); setActiveId(null); setAdminOpen(false); refresh(); toast.success("You left the workspace"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  const deleteWorkspace = async () => {
    try { await api.delete(`/workspaces/${activeId}`); setActiveId(null); setDeleteOpen(false); setAdminOpen(false); refresh(); toast.success("Workspace deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="workspace-switcher" className="rounded-full gap-1.5 h-9 max-w-[180px]">
            {activeId ? <Users className="w-4 h-4 text-primary shrink-0" /> : <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
            <span className="truncate font-semibold">{activeWorkspace?.name || "Personal"}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56" data-testid="workspace-menu">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Switch context</DropdownMenuLabel>
          <DropdownMenuItem data-testid="ws-personal" onClick={() => setActiveId(null)} className="cursor-pointer">
            <UserIcon className="w-4 h-4 mr-2 text-muted-foreground" /> Personal
            {!activeId && <Check className="w-4 h-4 ml-auto text-primary" />}
          </DropdownMenuItem>
          {workspaces.map((w) => (
            <DropdownMenuItem key={w.id} data-testid={`ws-item-${w.id}`} onClick={() => setActiveId(w.id)} className="cursor-pointer">
              <Users className="w-4 h-4 mr-2 text-primary" />
              <span className="truncate">{w.name}</span>
              {activeId === w.id && <Check className="w-4 h-4 ml-auto text-primary" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {activeId && (
            <DropdownMenuItem data-testid="ws-manage" onClick={() => setAdminOpen(true)} className="cursor-pointer">
              <Settings2 className="w-4 h-4 mr-2 text-muted-foreground" /> Manage team
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="ws-new" onClick={() => setNewOpen(true)} className="cursor-pointer">
            <Plus className="w-4 h-4 mr-2 text-muted-foreground" /> New workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* New workspace */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="new-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> New workspace</DialogTitle>
            <DialogDescription>Create a team space where roots are shared with everyone you invite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label>Workspace name</Label>
            <Input data-testid="new-workspace-input" placeholder="e.g. Design Team" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createWorkspace(); }} autoFocus />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createWorkspace} data-testid="create-workspace-btn" className="bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5"><Plus className="w-4 h-4" /> Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin panel */}
      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="team-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> {activeWorkspace?.name}</DialogTitle>
            <DialogDescription>Manage members and roles for this workspace.</DialogDescription>
          </DialogHeader>

          {isAdmin && (
            <div className="space-y-2">
              <Label>Rename workspace</Label>
              <div className="flex gap-2">
                <Input data-testid="ws-rename-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} />
                <Button variant="outline" onClick={rename} data-testid="ws-rename-btn" className="shrink-0">Save</Button>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="space-y-2 pt-1">
              <Label>Invite members</Label>
              <div className="flex gap-2">
                <Input data-testid="ws-invite-input" placeholder="email@company.com" value={inviteInput} onChange={(e) => setInviteInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") invite(); }} />
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="w-28 shrink-0" data-testid="ws-invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={invite} data-testid="ws-invite-btn" className="bg-primary hover:bg-[#E85D04] text-white shrink-0">Invite</Button>
              </div>
            </div>
          )}

          <div className="space-y-1.5 pt-1">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Members</Label>
            <div className="rounded-xl border border-border divide-y divide-border" data-testid="ws-members-list">
              {members.map((m) => {
                const RIcon = ROLE_ICON[m.role] || UserIcon;
                const canManage = isAdmin && m.role !== "owner" && m.user_id !== user.user_id;
                return (
                  <div key={m.user_id} data-testid={`ws-member-${m.user_id}`} className="flex items-center gap-2 px-3 py-2">
                    <RIcon className={`w-4 h-4 shrink-0 ${m.role === "owner" ? "text-amber-500" : m.role === "admin" ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{m.name}{m.user_id === user.user_id ? " (you)" : ""}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    {canManage ? (
                      <Select value={m.role} onValueChange={(v) => setRole(m.user_id, v)}>
                        <SelectTrigger className="w-24 h-8 text-xs" data-testid={`ws-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs font-semibold capitalize text-muted-foreground shrink-0">{m.role}</span>
                    )}
                    {canManage && (
                      <Button size="icon" variant="ghost" data-testid={`ws-remove-${m.user_id}`} onClick={() => removeMember(m.user_id)} className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {invites.map((i) => (
                <div key={i.email} data-testid={`ws-invite-${i.email}`} className="flex items-center gap-2 px-3 py-2 opacity-70">
                  <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1"><div className="text-sm truncate">{i.email}</div><div className="text-xs text-muted-foreground">Invited · {i.role}</div></div>
                  <span className="text-xs font-semibold text-amber-600 shrink-0">Pending</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-2">
            {!isOwner ? (
              <Button variant="ghost" onClick={leaveWorkspace} data-testid="ws-leave-btn" className="text-muted-foreground hover:text-destructive gap-1.5"><LogOut className="w-4 h-4" /> Leave</Button>
            ) : <span />}
            {isOwner && (
              <Button variant="ghost" onClick={() => setDeleteOpen(true)} data-testid="ws-delete-btn" className="text-destructive hover:bg-destructive/10 gap-1.5"><Trash2 className="w-4 h-4" /> Delete workspace</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="ws-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this workspace?</AlertDialogTitle>
            <AlertDialogDescription>All team roots and memberships in <b>{activeWorkspace?.name}</b> will be permanently removed for everyone. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteWorkspace} data-testid="ws-delete-confirm-btn" className="bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
